import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Box, Typography, IconButton, Drawer, List, ListItem,
  ListItemText, ListItemButton, Divider, CircularProgress, Tooltip,
  Paper, Chip
} from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import DownloadIcon from "@mui/icons-material/Download";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CloseIcon from "@mui/icons-material/Close";
import MenuIcon from "@mui/icons-material/Menu";
import { useParams } from "react-router-dom";
import { fetchData } from "../../BaseUrl";

// ─── CSS injected for page-flip animation ────────────────────────────────────
const FLIP_STYLES = `
@keyframes pageFlipForward {
  0%   { transform: rotateY(0deg);    }
  100% { transform: rotateY(-180deg); }
}
@keyframes pageFlipBackward {
  0%   { transform: rotateY(-180deg); }
  100% { transform: rotateY(0deg);    }
}
.page-flip-forward {
  animation: pageFlipForward 0.45s cubic-bezier(0.455, 0.03, 0.515, 0.955) forwards;
  transform-origin: left center;
  transform-style: preserve-3d;
  backface-visibility: hidden;
}
.page-flip-backward {
  animation: pageFlipBackward 0.45s cubic-bezier(0.455, 0.03, 0.515, 0.955) forwards;
  transform-origin: right center;
  transform-style: preserve-3d;
  backface-visibility: hidden;
}
`;

// ─── Fallback pages ──────────────────────────────────────────────────────────
const FALLBACK_COLORS = ["#EAF1F8", "#e0f2fe", "#fef3c7", "#f0fdf4", "#fce7f3", "#ffedd5"];
const FALLBACK_PAGES = [
  { pageNumber: 1, imageUrl: null, content: "Welcome to Cummings Tamil School\n\nStudent Handbook 2025–26\n\nThis handbook contains all the policies, guidelines, and information you need for a successful academic year.\n\nPlease read it carefully with your family." },
  { pageNumber: 2, imageUrl: null, content: "Table of Contents\n\n1. School Mission & Vision ......... 3\n2. Enrollment Procedures ........... 5\n3. Attendance Policy ................... 7\n4. Code of Conduct ..................... 9\n5. Academic Calendar ............... 11\n6. Parent Communication ......... 13" },
  { pageNumber: 3, imageUrl: null, content: "Mission Statement\n\nCummings Tamil School is dedicated to preserving the Tamil language, culture, and heritage for the diaspora community.\n\nVision\n\nTo nurture culturally grounded, academically strong Tamil-speaking students who are proud of their roots." },
  { pageNumber: 4, imageUrl: null, content: "Enrollment Procedures\n\n1. Complete online registration form\n2. Attend evaluation session\n3. Confirm payment of fees\n4. Receive class assignment\n5. Attend orientation day\n\nFor questions, contact our registrar at register@ctstamilschool.org" },
  { pageNumber: 5, imageUrl: null, content: "Attendance Policy\n\nRegular attendance is essential for language learning.\n\n- 3 absences: Parent notification\n- 5 absences: Academic review\n- 8+ absences: May not qualify for promotion\n\nPlease notify the teacher in advance if your child will be absent." },
  { pageNumber: 6, imageUrl: null, content: "Academic Calendar 2025–26\n\nFirst Day of School: August 24, 2025\nFall Break: October 13–17, 2025\nWinter Break: December 22 – January 5\nSpring Break: March 16–20, 2026\nLast Day of School: May 22, 2026\n\nSpecial Events:\n- Annual Day: April 18, 2026\n- Parent Day: February 14, 2026" },
];

const FALLBACK_DOC = {
  title: "Student Handbook 2025–26",
  pages: FALLBACK_PAGES,
  pdfUrl: null,
};

// ─── Single page renderer ────────────────────────────────────────────────────
const PageView = ({ page, index, isFlipping, flipDir, zoom }) => {
  const bg = FALLBACK_COLORS[index % FALLBACK_COLORS.length];
  const animClass = isFlipping
    ? flipDir === "forward" ? "page-flip-forward" : "page-flip-backward"
    : "";

  return (
    <Box
      className={animClass}
      sx={{
        width: "100%",
        height: "100%",
        backgroundColor: page?.imageUrl ? "transparent" : bg,
        backgroundImage: page?.imageUrl ? `url(${page.imageUrl})` : "none",
        backgroundSize: "cover",
        backgroundPosition: "center",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: page?.imageUrl ? "flex-end" : "center",
        p: page?.imageUrl ? 1 : 3,
        boxSizing: "border-box",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Page number badge */}
      <Box
        sx={{
          position: "absolute",
          bottom: 12,
          ...(index % 2 === 0 ? { right: 16 } : { left: 16 }),
          backgroundColor: "rgba(255,255,255,0.85)",
          borderRadius: "20px",
          px: 1.5, py: 0.25,
          fontSize: "11px",
          color: "#374151",
          fontWeight: 600,
          boxShadow: "0 1px 4px rgba(0,0,0,0.12)",
        }}
      >
        {page?.pageNumber}
      </Box>

      {/* Content for text-based pages */}
      {!page?.imageUrl && page?.content && (
        <Typography
          sx={{
            fontSize: `${12 * zoom}px`,
            color: "#374151",
            whiteSpace: "pre-line",
            lineHeight: 1.7,
            textAlign: "left",
            width: "100%",
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          {page.content}
        </Typography>
      )}

      {/* Empty page placeholder */}
      {!page && (
        <Typography sx={{ fontSize: "13px", color: "#9CA3AF" }}>—</Typography>
      )}
    </Box>
  );
};

// ─── Main component ──────────────────────────────────────────────────────────
const Flipbook = () => {
  const { documentId } = useParams();
  const [document, setDocument]   = useState(null);
  const [loading, setLoading]     = useState(true);
  const [currentSpread, setCurrentSpread] = useState(0); // 0-based spread index (2 pages per spread)
  const [zoom, setZoom]           = useState(1.0);
  const [tocOpen, setTocOpen]     = useState(false);
  const [flipping, setFlipping]   = useState(false);
  const [flipDir, setFlipDir]     = useState("forward");
  const flipTimerRef              = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetchData(`public/flipbook/${documentId}`);
      const doc = res?.data || res;
      if (doc && doc.pages && doc.pages.length > 0) {
        setDocument(doc);
      } else {
        setDocument(FALLBACK_DOC);
      }
    } catch (_) {
      setDocument(FALLBACK_DOC);
    } finally {
      setLoading(false);
    }
  }, [documentId]);

  useEffect(() => {
    load();
    // Inject CSS once
    if (!document.getElementById("flipbook-styles")) {
      const style = window.document.createElement("style");
      style.id = "flipbook-styles";
      style.textContent = FLIP_STYLES;
      window.document.head.appendChild(style);
    }
    return () => {
      if (flipTimerRef.current) clearTimeout(flipTimerRef.current);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);

  const pages      = document?.pages || [];
  const totalPages = pages.length;

  // Each spread shows 2 pages: spread 0 → pages 0,1 | spread 1 → pages 2,3 etc.
  const leftPageIdx  = currentSpread * 2;
  const rightPageIdx = currentSpread * 2 + 1;
  const leftPage     = pages[leftPageIdx]  || null;
  const rightPage    = pages[rightPageIdx] || null;
  const maxSpread    = Math.ceil(totalPages / 2) - 1;

  const triggerFlip = (dir) => {
    if (flipping) return;
    setFlipDir(dir);
    setFlipping(true);
    flipTimerRef.current = setTimeout(() => {
      setFlipping(false);
      if (dir === "forward") setCurrentSpread(s => Math.min(s + 1, maxSpread));
      else                   setCurrentSpread(s => Math.max(s - 1, 0));
    }, 450);
  };

  const handlePrev = () => { if (currentSpread > 0)        triggerFlip("backward"); };
  const handleNext = () => { if (currentSpread < maxSpread) triggerFlip("forward");  };

  const handleZoomIn  = () => setZoom(z => Math.min(z + 0.25, 2.0));
  const handleZoomOut = () => setZoom(z => Math.max(z - 0.25, 0.5));

  const handleDownload = () => {
    if (document?.pdfUrl) window.open(document.pdfUrl, "_blank");
    else alert("PDF not available for this document.");
  };

  const handleTocJump = (spreadIdx) => {
    setCurrentSpread(spreadIdx);
    setTocOpen(false);
  };

  const displayedLeftPage  = (flipDir === "backward" && flipping) ? pages[leftPageIdx  - 2] || null : leftPage;
  const displayedRightPage = (flipDir === "forward"  && flipping) ? pages[rightPageIdx + 2] || null : rightPage;

  return (
    <Box
      sx={{
        minHeight: "100vh",
        backgroundColor: "#1a1a2e",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        p: 2,
        fontFamily: "'Inter', 'Roboto', sans-serif",
      }}
    >
      {loading ? (
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: "100vh" }}>
          <CircularProgress sx={{ color: "#4A7BA8" }} />
        </Box>
      ) : (
        <>
          {/* Top Bar */}
          <Paper
            elevation={0}
            sx={{
              width: "100%",
              maxWidth: 960,
              borderRadius: 3,
              backgroundColor: "rgba(255,255,255,0.08)",
              backdropFilter: "blur(10px)",
              border: "1px solid rgba(255,255,255,0.12)",
              px: 3, py: 1.5,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              mb: 2,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            {/* Left: TOC toggle + title */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <Tooltip title="Table of Contents">
                <IconButton onClick={() => setTocOpen(true)} sx={{ color: "#4A7BA8" }}>
                  <MenuIcon />
                </IconButton>
              </Tooltip>
              <MenuBookIcon sx={{ color: "#4A7BA8", fontSize: "20px" }} />
              <Typography sx={{ color: "#fff", fontWeight: 700, fontSize: "15px" }}>
                {document?.title || "Document Viewer"}
              </Typography>
            </Box>

            {/* Center: Page counter */}
            <Chip
              label={`Page ${leftPageIdx + 1}${rightPage ? `–${rightPageIdx + 1}` : ""} of ${totalPages}`}
              sx={{ backgroundColor: "rgba(74, 123, 168,0.25)", color: "#A9C2DB", fontWeight: 600, fontSize: "12px" }}
            />

            {/* Right: Controls */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
              <Tooltip title={`Zoom Out (${Math.round(zoom * 100)}%)`}>
                <span>
                  <IconButton onClick={handleZoomOut} disabled={zoom <= 0.5} sx={{ color: zoom <= 0.5 ? "#555" : "#4A7BA8" }}>
                    <ZoomOutIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Typography sx={{ color: "#9CA3AF", fontSize: "12px", minWidth: "40px", textAlign: "center" }}>
                {Math.round(zoom * 100)}%
              </Typography>
              <Tooltip title="Zoom In">
                <span>
                  <IconButton onClick={handleZoomIn} disabled={zoom >= 2.0} sx={{ color: zoom >= 2.0 ? "#555" : "#4A7BA8" }}>
                    <ZoomInIcon />
                  </IconButton>
                </span>
              </Tooltip>
              <Divider orientation="vertical" flexItem sx={{ borderColor: "rgba(255,255,255,0.15)", mx: 0.5 }} />
              <Tooltip title="Download PDF">
                <IconButton onClick={handleDownload} sx={{ color: "#4A7BA8" }}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Paper>

          {/* Book Container */}
          <Box
            sx={{
              width:     `${900 * zoom}px`,
              height:    `${600 * zoom}px`,
              maxWidth:  "100%",
              perspective: "1200px",
              position:  "relative",
              transition: "width 0.3s, height 0.3s",
            }}
          >
            <Box
              sx={{
                width:  "100%",
                height: "100%",
                display: "flex",
                position: "relative",
                borderRadius: "4px 12px 12px 4px",
                boxShadow: "0 20px 60px rgba(0,0,0,0.5), 0 4px 20px rgba(0,0,0,0.3)",
                overflow: "hidden",
              }}
            >
              {/* Left Page */}
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "4px 0 0 4px",
                  boxShadow: "inset -4px 0 8px rgba(0,0,0,0.15)",
                  backgroundColor: "#fff",
                }}
              >
                {leftPage ? (
                  <PageView
                    page={flipping && flipDir === "backward" ? displayedLeftPage : leftPage}
                    index={leftPageIdx}
                    isFlipping={flipping && flipDir === "backward"}
                    flipDir={flipDir}
                    zoom={zoom}
                  />
                ) : (
                  <Box sx={{ width: "100%", height: "100%", backgroundColor: "#f9f9f9", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ color: "#D1D5DB", fontSize: "13px" }}>No page</Typography>
                  </Box>
                )}
              </Box>

              {/* Spine */}
              <Box
                sx={{
                  width: "4px",
                  height: "100%",
                  flexShrink: 0,
                  background: "linear-gradient(to bottom, #A9C2DB 0%, #244C77 50%, #A9C2DB 100%)",
                  boxShadow: "0 0 8px rgba(80,56,237,0.5)",
                  zIndex: 2,
                }}
              />

              {/* Right Page */}
              <Box
                sx={{
                  width: "50%",
                  height: "100%",
                  position: "relative",
                  overflow: "hidden",
                  borderRadius: "0 12px 12px 0",
                  boxShadow: "inset 4px 0 8px rgba(0,0,0,0.15)",
                  backgroundColor: "#fff",
                }}
              >
                {rightPage ? (
                  <PageView
                    page={flipping && flipDir === "forward" ? displayedRightPage : rightPage}
                    index={rightPageIdx}
                    isFlipping={flipping && flipDir === "forward"}
                    flipDir={flipDir}
                    zoom={zoom}
                  />
                ) : (
                  <Box sx={{ width: "100%", height: "100%", backgroundColor: "#fafafa", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    <Typography sx={{ color: "#D1D5DB", fontSize: "13px" }}>End of document</Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Box>

          {/* Navigation Controls */}
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 3,
              mt: 3,
            }}
          >
            <Tooltip title="Previous spread">
              <span>
                <IconButton
                  onClick={handlePrev}
                  disabled={currentSpread === 0 || flipping}
                  sx={{
                    backgroundColor: currentSpread === 0 ? "rgba(255,255,255,0.05)" : "rgba(74, 123, 168,0.25)",
                    color: currentSpread === 0 ? "#555" : "#4A7BA8",
                    width: 48, height: 48,
                    "&:hover": { backgroundColor: "rgba(74, 123, 168,0.4)" },
                  }}
                >
                  <ArrowBackIosNewIcon />
                </IconButton>
              </span>
            </Tooltip>

            {/* Spread dots */}
            <Box sx={{ display: "flex", gap: 0.75, alignItems: "center" }}>
              {Array.from({ length: maxSpread + 1 }, (_, i) => (
                <Box
                  key={i}
                  onClick={() => !flipping && setCurrentSpread(i)}
                  sx={{
                    width:  i === currentSpread ? 20 : 8,
                    height: 8,
                    borderRadius: "4px",
                    backgroundColor: i === currentSpread ? "#4A7BA8" : "rgba(255,255,255,0.25)",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    "&:hover": { backgroundColor: "#4A7BA8" },
                  }}
                />
              ))}
            </Box>

            <Tooltip title="Next spread">
              <span>
                <IconButton
                  onClick={handleNext}
                  disabled={currentSpread >= maxSpread || flipping}
                  sx={{
                    backgroundColor: currentSpread >= maxSpread ? "rgba(255,255,255,0.05)" : "rgba(74, 123, 168,0.25)",
                    color: currentSpread >= maxSpread ? "#555" : "#4A7BA8",
                    width: 48, height: 48,
                    "&:hover": { backgroundColor: "rgba(74, 123, 168,0.4)" },
                  }}
                >
                  <ArrowForwardIosIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* Page label below */}
          <Typography sx={{ color: "rgba(255,255,255,0.4)", fontSize: "12px", mt: 1.5 }}>
            Spread {currentSpread + 1} of {maxSpread + 1}
          </Typography>
        </>
      )}

      {/* TOC Drawer */}
      <Drawer
        anchor="left"
        open={tocOpen}
        onClose={() => setTocOpen(false)}
        PaperProps={{
          sx: {
            width: 280,
            backgroundColor: "#1e1b4b",
            color: "#fff",
          }
        }}
      >
        <Box sx={{ p: 2, display: "flex", justifyContent: "space-between", alignItems: "center", borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <ListAltIcon sx={{ color: "#4A7BA8", fontSize: "20px" }} />
            <Typography sx={{ fontWeight: 700, color: "#fff" }}>Table of Contents</Typography>
          </Box>
          <IconButton size="small" onClick={() => setTocOpen(false)} sx={{ color: "#9CA3AF" }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ p: 1 }}>
          <Typography sx={{ fontSize: "12px", color: "#9CA3AF", px: 1, pb: 1 }}>
            {document?.title}
          </Typography>
        </Box>
        <List dense sx={{ px: 1 }}>
          {Array.from({ length: Math.ceil(pages.length / 2) }, (_, i) => {
            const p1 = pages[i * 2];
            const p2 = pages[i * 2 + 1];
            const isActive = i === currentSpread;
            return (
              <ListItem key={i} disablePadding>
                <ListItemButton
                  onClick={() => handleTocJump(i)}
                  sx={{
                    borderRadius: "8px",
                    mb: 0.5,
                    backgroundColor: isActive ? "rgba(74, 123, 168,0.25)" : "transparent",
                    "&:hover": { backgroundColor: "rgba(74, 123, 168,0.15)" },
                  }}
                >
                  <Box sx={{
                    width: 24, height: 24, borderRadius: "6px",
                    backgroundColor: isActive ? "#4A7BA8" : "rgba(255,255,255,0.1)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    mr: 1.5, flexShrink: 0,
                  }}>
                    <Typography sx={{ fontSize: "10px", fontWeight: 700, color: isActive ? "#fff" : "#9CA3AF" }}>
                      {i + 1}
                    </Typography>
                  </Box>
                  <ListItemText
                    primary={`Pages ${p1?.pageNumber || i * 2 + 1}${p2 ? `–${p2.pageNumber}` : ""}`}
                    secondary={p1?.content ? p1.content.split("\n")[0].substring(0, 30) + "…" : ""}
                    primaryTypographyProps={{ fontSize: "13px", color: isActive ? "#A9C2DB" : "#E5E7EB", fontWeight: isActive ? 600 : 400 }}
                    secondaryTypographyProps={{ fontSize: "11px", color: "#6B7280" }}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      </Drawer>
    </Box>
  );
};

export default Flipbook;
